/**
 * AdminLayout - Wrapper component for admin pages
 *
 * Provides:
 * 1. Access control (renders NotFound if not allowed)
 * 2. Consistent admin page layout
 * 3. Navigation between admin sections
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Shield, Cpu, BarChart3, Settings, Home, ChevronRight, Layers, ListChecks, RefreshCcw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBackNavigation } from '../../hooks/useBackNavigation';
import { isAdminUiAllowed } from '../../utils/adminAuth';

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
  description?: string;
}

const NAV_ITEMS = [
  { path: '/admin', label: 'Overview', icon: Home },
  { path: '/admin/cohorts', label: 'Cohort Builder', icon: Layers },
  { path: '/admin/ai-tagging', label: 'AI Tagging', icon: Cpu },
  { path: '/admin/jobs', label: 'Jobs', icon: ListChecks },
  { path: '/admin/sync', label: 'Sync', icon: RefreshCcw },
  { path: '/admin/completeness', label: 'Completeness', icon: BarChart3 },
  { path: '/admin/config', label: 'Config', icon: Settings },
];

export function AdminLayout({ children, title, description }: AdminLayoutProps) {
  const { user } = useAuth();
  const location = useLocation();
  const goBack = useBackNavigation('/admin');

  // Gate: if not allowed, render a generic not-found page
  if (!isAdminUiAllowed(user)) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-2">404</h1>
          <p className="text-slate-400">Page not found</p>
          <button
            onClick={goBack}
            className="mt-4 inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <Link
            to="/"
            className="mt-3 block text-teal-400 hover:text-teal-300"
          >
            Go home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1419]">
      {/* Admin Header */}
      <div className="bg-[#1A1F26] border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Shield size={20} className="text-orange-400" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-white">Admin Console</h1>
                <p className="text-xs text-slate-400">Operator access only</p>
              </div>
            </div>
            <Link
              to="/"
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              Exit Admin
            </Link>
          </div>
        </div>
      </div>

      {/* Admin Navigation */}
      <div className="bg-[#1A1F26]/50 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1 -mb-px">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? 'border-teal-400 text-teal-400'
                      : 'border-transparent text-slate-400 hover:text-white hover:border-slate-600'
                    }
                  `}
                >
                  <Icon size={16} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-3"
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Back</span>
        </button>

        <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
          <Link to="/admin" className="hover:text-white">Admin</Link>
          <ChevronRight size={14} />
          <span className="text-white">{title}</span>
        </div>
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        {description && (
          <p className="mt-1 text-slate-400">{description}</p>
        )}
      </div>

      {/* Page Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {children}
      </div>
    </div>
  );
}
